import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { EventLogItemType, UserAction } from "../models/enums";
import { EventLog } from "../models/event/event-log";
import { buildUrl } from "../shared/url.utils";

/** Service to handle event log API and display formatting */
@Injectable({
  providedIn: "root",
})
export class EventLogService {
  private readonly apiSegment = "EventLog";
  private readonly headers = { "Api-Version": environment.eventLogApi.version };

  constructor(private httpClient: HttpClient) {}

  /** Get event logs for specified event as displayable text strings */
  getEventLogs(eventId: number): Observable<string[]> {
    return this.httpClient
      .get<EventLog[]>(
        buildUrl([
          environment.eventLogApi.path,
          this.apiSegment,
          `?eventId=${eventId}`,
        ]),
        { headers: this.headers }
      )
      .pipe(
        map((logArray: EventLog[]) =>
          logArray.map((logItem) => this.createTextLog(logItem))
        )
      );
  }

  /** Convert an EventLog item to a displayable text string */
  private createTextLog(logItem: EventLog): string {
    const dateText = this.getDateString(logItem.timeStamp);
    let actionText =
      logItem.eventLogItemType !== EventLogItemType.Event
        ? `${this.numberToString(logItem.itemQuantity)} `
        : "";
    actionText +=
      logItem.userAction === UserAction.Add &&
      logItem.eventLogItemType !== EventLogItemType.Event
        ? logItem.itemQuantity > 1
          ? "nya "
          : "ny "
        : "";
    actionText += `${this.getLogItemTypeString(logItem)} `;
    actionText += `${this.getUserActionString(logItem.userAction)} `;
    actionText += `av ${logItem.userFullName}`;
    actionText.trim();
    actionText = actionText.substr(0, 1).toUpperCase() + actionText.substr(1);
    return `${dateText} ${actionText}.`;
  }

  private getDateString(date: Date): string {
    date = new Date(date);
    return `${date.toLocaleDateString("sv-SE")} ${date.toLocaleTimeString(
      "sv-SE",
      {
        hour: "2-digit",
        minute: "2-digit",
      }
    )}.`;
  }

  private getLogItemTypeString(log: EventLog): string {
    if (log.eventLogItemType === EventLogItemType.Event) {
      return "händelsen";
    }
    if (log.eventLogItemType === EventLogItemType.Announcement) {
      return log.itemQuantity > 1 ? "annonseringar" : "annonsering";
    }
    if (log.eventLogItemType === EventLogItemType.TrafficAction) {
      return log.itemQuantity > 1 ? "trafikåtgärder" : "trafikåtgärd";
    }
    return "";
  }

  private getUserActionString(action: UserAction): string {
    switch (action) {
      case UserAction.Create:
        return "har skapats";
      case UserAction.Add:
        return "har lagts till";
      case UserAction.Remove:
        return "har tagits bort";
      case UserAction.Update:
        return "har uppdaterats";
      case UserAction.Send:
        return "har skickats till Trafikverket";
      default:
        return "";
    }
  }

  private numberToString(num: number): string {
    const numberAsWords = [
      "",
      "en",
      "två",
      "tre",
      "fyra",
      "fem",
      "sex",
      "sju",
      "åtta",
      "nio",
    ];
    return numberAsWords[num] ?? "" + num;
  }
}
